import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { HeaderLogo } from "@/components/Logo";

const NAV = [
  { label: "Services", to: "/services", testid: "nav-services-link" },
  { label: "Approach", to: "/approach", testid: "nav-approach-link" },
  { label: "About", to: "/about", testid: "nav-about-link" },
  { label: "Letters", to: "/letters", testid: "nav-letters-link" },
  { label: "FAQ", to: "/faq", testid: "nav-faq-link" },
  { label: "Contact", to: "/contact", testid: "nav-contact-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-500 border-b border-white/10 bg-steel/95 backdrop-blur-xl ${
          scrolled ? "shadow-[0_2px_30px_rgba(0,0,0,0.3)]" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-6">
          <Link to="/" aria-label="T.A.L.K. home" data-testid="nav-home-link" className="hover:opacity-80 transition-opacity duration-300">
            <HeaderLogo />
          </Link>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                data-testid={item.testid}
                className="gold-underline-link text-[11px] uppercase tracking-[0.2em] text-offwhite/85 hover:text-goldsoft transition-colors duration-300"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/booking"
              data-testid="nav-book-session-button"
              className="bg-goldsoft text-steel text-[10px] uppercase tracking-[0.24em] px-5 py-3 hover:bg-offwhite transition-colors duration-300"
            >
              Book a Session
            </Link>
          </nav>
          <button
            className="lg:hidden text-offwhite p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-button"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] bg-steel flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
              <HeaderLogo className="h-11" />
              <button
                className="text-offwhite p-2"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1 overflow-y-auto py-10" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-${item.testid}`}
                    className="font-display text-4xl text-offwhite py-2.5 block hover:text-goldsoft transition-colors duration-300"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="pt-8"
              >
                <Link
                  to="/booking"
                  data-testid="mobile-book-session-button"
                  className="inline-block bg-goldsoft text-steel text-xs uppercase tracking-[0.24em] font-semibold px-8 py-4"
                >
                  Book a Session
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
